'use client'

import {useCallback, useEffect, useRef, useState} from 'react'
import {AudioLines, Loader2, Play} from 'lucide-react'
import {TTS_VOICE_MODELS, type TtsVoiceModel} from '@/shared'
import type {SettingsState} from '@/types'
import {PageHeader} from '@/components/PageHeader'
import {Switch} from '@/components/Switch'
import {apiGet, apiPost, getAuthToken} from '@/lib/api'
import {message} from '@/components/Toast'

/**
 * Voice-changer page. Under the new STT → TTS pipeline the "model" a
 * user picks is simply an Edge-TTS voice preset (the same catalogue the
 * 文字转语音 page surfaces). The server handles:
 *
 *   1. decoding the outgoing voice message,
 *   2. running Faster-Whisper to transcribe it,
 *   3. resynthesising with the selected edge-tts voice.
 *
 * The page UI therefore only needs a master switch, a horizontal row of
 * voice cards, and a preview (Play) button per card that plays the
 * synthesized sample right in the browser.
 */

const PREVIEW_TEXT = '你好，这是一条变声测试语音。'

type SynthesizeResponse = {
    success: boolean
    base64?: string
    mimeType?: string
    format?: 'wav' | 'mp3'
    playLength?: number
}

export function VoiceChangerView({
    settings,
    saveSettings,
}: {
    settings: SettingsState
    saveSettings: (patch: Partial<SettingsState>) => Promise<void> | void
}) {
    const voiceChanger = settings.voiceChanger || {enabled: false, selectedModelId: ''}
    const [previewBusy, setPreviewBusy] = useState('')
    const [enabling, setEnabling] = useState(false)
    const [installProgress, setInstallProgress] = useState<{stage: string; progress: number; message?: string} | null>(null)
    const [runtimeReady, setRuntimeReady] = useState(true)
    const [runtimeMessage, setRuntimeMessage] = useState('')
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const evtSourceRef = useRef<EventSource | null>(null)

    // On mount, check if install is in progress and reconnect to SSE
    useEffect(() => {
        let cancelled = false
        apiGet<{installing?: boolean; runtime?: {runtimeReady?: boolean; dependenciesReady?: boolean; message?: string}; message?: string}>('/voice-changer/status', {silent: true})
            .then((data) => {
                if (cancelled) return
                const ready = data.runtime?.runtimeReady !== false
                setRuntimeReady(ready)
                setRuntimeMessage(data.runtime?.message || data.message || '')
                if (data.installing) {
                    setEnabling(true)
                    connectProgressSSE()
                }
            })
            .catch(() => { /* ignore */ })
        return () => {
            cancelled = true
            evtSourceRef.current?.close()
        }
    }, [])

    function connectProgressSSE() {
        evtSourceRef.current?.close()
        const token = getAuthToken()
        const params = new URLSearchParams()
        if (token) params.set('access_token', token)
        const query = params.toString()
        const evtSource = new EventSource(`/api/voice-changer/install/progress${query ? `?${query}` : ''}`)
        evtSourceRef.current = evtSource
        evtSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data) as {stage: string; progress: number}
                setInstallProgress(data)
                if (data.stage === 'done') {
                    evtSource.close()
                    setEnabling(false)
                    setInstallProgress(null)
                } else if (data.stage === 'error') {
                    evtSource.close()
                    setEnabling(false)
                    setInstallProgress(null)
                }
            } catch { /* ignore */ }
        }
        evtSource.onerror = () => {
            evtSource.close()
        }
    }

    const stopPreview = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.src = ''
            audioRef.current = null
        }
    }, [])

    useEffect(() => stopPreview, [stopPreview])

    const activeId = voiceChanger.selectedModelId || TTS_VOICE_MODELS[0]?.id || ''

    async function handleApply(voice: TtsVoiceModel) {
        if (voiceChanger.selectedModelId !== voice.id) {
            await saveSettings({
                voiceChanger: {enabled: voiceChanger.enabled, selectedModelId: voice.id},
            })
        }
    }

    async function handleToggleEnabled() {
        const next = !voiceChanger.enabled
        if (next && !activeId) {
            message.error('请先选择一个声音模型')
            return
        }
        if (!next) {
            await saveSettings({voiceChanger: {...voiceChanger, enabled: false}})
            return
        }
        if (enabling) return
        setEnabling(true)
        setInstallProgress(null)

        // Subscribe to SSE progress
        connectProgressSSE()

        try {
            await apiPost<{success: boolean}>('/voice-changer/install', {})
            await saveSettings({voiceChanger: {...voiceChanger, enabled: true}})
        } catch {
            /* toast fired by api.ts */
        } finally {
            evtSourceRef.current?.close()
            setEnabling(false)
            setInstallProgress(null)
        }
    }

    async function handlePreview(voice: TtsVoiceModel) {
        if (previewBusy) return
        stopPreview()
        setPreviewBusy(voice.id)
        try {
            const response = await apiPost<SynthesizeResponse>('/tts/synthesize', {
                text: PREVIEW_TEXT,
                voice: voice.id,
            })
            if (!response.success || !response.base64) {
                message.error('合成失败')
                return
            }
            const audio = new Audio(response.base64)
            audioRef.current = audio
            audio.play().catch(() => {
                message.error('浏览器阻止了自动播放，请点击页面后再次尝试')
            })
        } catch {
            /* toast fired by api.ts */
        } finally {
            setPreviewBusy('')
        }
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="变声器"
                description="开启后，您发送的语音会自动转写为文字并由所选声音重新合成。"
            />
            <div className="flex-1 overflow-y-auto p-6">
                <div className="bg-white rounded-card shadow-card border border-hair w-full max-w-5xl p-8">
                    <div className="flex items-center justify-between mb-8 pb-6 border-b border-hair">
                        <div className="flex items-center gap-3">
                            <AudioLines size={24} className="text-accent"/>
                            <h3 className="text-lg font-medium text-ink tracking-wide">变声器功能</h3>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center gap-1.5 text-sm text-soft font-medium">
                                {enabling ? (
                                    <>
                                        {installProgress?.stage === 'download' ? (
                                            <>
                                                下载资源
                                                <span className="relative inline-flex items-center justify-center w-6 h-6">
                                                    <svg className="w-6 h-6 -rotate-90" viewBox="0 0 24 24">
                                                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.2"/>
                                                        <circle
                                                            cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"
                                                            strokeDasharray={`${(installProgress.progress / 100) * 62.83} 62.83`}
                                                            strokeLinecap="round"
                                                            className="text-accent"
                                                        />
                                                    </svg>
                                                    <span className="absolute text-[8px] font-bold text-accent">{installProgress.progress}</span>
                                                </span>
                                            </>
                                        ) : installProgress?.stage === 'extract' ? (
                                            <>
                                                解压中
                                                <Loader2 size={14} className="animate-spin"/>
                                            </>
                                        ) : installProgress?.stage === 'pip' ? (
                                            <>
                                                {installProgress.message || '安装依赖'}
                                                {installProgress.progress > 0 && installProgress.progress < 100 ? (
                                                    <span className="relative inline-flex items-center justify-center w-6 h-6">
                                                        <svg className="w-6 h-6 -rotate-90" viewBox="0 0 24 24">
                                                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.2"/>
                                                            <circle
                                                                cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"
                                                                strokeDasharray={`${(installProgress.progress / 100) * 62.83} 62.83`}
                                                                strokeLinecap="round"
                                                                className="text-accent"
                                                            />
                                                        </svg>
                                                        <span className="absolute text-[8px] font-bold text-accent">{installProgress.progress}</span>
                                                    </span>
                                                ) : (
                                                    <Loader2 size={14} className="animate-spin"/>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                开启中
                                                <Loader2 size={14} className="animate-spin"/>
                                            </>
                                        )}
                                    </>
                                ) : !runtimeReady && runtimeMessage ? (
                                    runtimeMessage
                                ) : voiceChanger.enabled ? (
                                    '已开启'
                                ) : (
                                    '已关闭'
                                )}
                            </span>
                            <Switch checked={voiceChanger.enabled} onChange={handleToggleEnabled} disabled={enabling}/>
                        </div>
                    </div>
                    <div className={`transition-opacity duration-300 ${voiceChanger.enabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                        <h4 className="text-[15px] font-medium text-soft mb-5">选择声音模型</h4>
                        <div className="flex gap-5 overflow-x-auto pb-6 pt-2">
                            {TTS_VOICE_MODELS.map((voice) => {
                                const isActive = activeId === voice.id
                                const isPreviewing = previewBusy === voice.id
                                return (
                                    <div
                                        key={voice.id}
                                        onClick={() => void handleApply(voice)}
                                        className={`shrink-0 w-48 border rounded-xl p-5 flex flex-col items-center cursor-pointer transition-all ${
                                            isActive
                                                ? 'border-accent bg-accent/5 shadow-card -translate-y-1'
                                                : 'border-hair hover:border-accent/50 hover:shadow-card bg-white'
                                        }`}
                                    >
                                        <div
                                            className="w-20 h-20 rounded-full mb-4 shadow-card border-2 border-white flex items-center justify-center text-2xl font-semibold text-ink"
                                            style={{background: voice.avatarBg || '#f0f0f0'}}
                                        >
                                            {voice.avatar}
                                        </div>
                                        <span className="font-medium text-ink text-[15px] text-center">
                                            {voice.name}
                                        </span>
                                        <span className="text-xs text-muted mt-1 mb-5 text-center truncate w-full">
                                            {voice.desc}
                                        </span>
                                        <div className="w-full flex items-center justify-center gap-2 mt-auto">
                                            {isActive ? (
                                                <span className="text-xs bg-accent text-white px-5 py-1.5 rounded-full font-medium">
                                                    使用中
                                                </span>
                                            ) : (
                                                <span className="text-xs border border-accent text-accent px-5 py-1.5 rounded-full font-medium hover:bg-accent/10 transition-colors">
                                                    应用该声音
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    void handlePreview(voice)
                                                }}
                                                disabled={!!previewBusy}
                                                title="试听"
                                                className="text-muted hover:text-accent transition-colors disabled:opacity-50"
                                            >
                                                {isPreviewing ? (
                                                    <Loader2 size={16} className="animate-spin"/>
                                                ) : (
                                                    <Play size={16}/>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
